/**
 * Toast UI Components
 *
 * Renders transient notification toasts in a fixed container.
 * Positioned bottom-right, stacking upward with slide-in animation.
 */

import { useEffect, useState } from 'react'
import { cn } from '../../lib/utils'
import { FiX, FiCheck, FiAlertTriangle, FiAlertCircle, FiInfo } from 'react-icons/fi'
import { useToastStore, type Toast, type ToastVariant } from '../../store/useToastStore'

// ── Variant styles ────────────────────────────────────────────────────

const variantStyles: Record<ToastVariant, string> = {
  default: 'bg-background border-border text-foreground',
  success: 'bg-green-50 border-green-200 text-green-900',
  error: 'bg-red-50 border-red-200 text-red-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
}

const variantIcons: Record<ToastVariant, React.ReactNode> = {
  default: <FiInfo className="text-primary shrink-0" size={18} />,
  success: <FiCheck className="text-green-600 shrink-0" size={18} />,
  error: <FiAlertCircle className="text-red-600 shrink-0" size={18} />,
  warning: <FiAlertTriangle className="text-amber-600 shrink-0" size={18} />,
}

// ── Single Toast ──────────────────────────────────────────────────────

function ToastItem({ toast }: { toast: Toast }) {
  const removeToast = useToastStore((s) => s.removeToast)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Trigger slide-in on next frame
    const raf = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  const handleDismiss = () => {
    setVisible(false)
    setTimeout(() => removeToast(toast.id), 200)
  }

  return (
    <div
      data-figma-component="Toast"
      role="alert"
      aria-live="polite"
      className={cn(
        'flex items-start gap-3 w-80 p-4 rounded-lg border shadow-lg transition-all duration-200',
        variantStyles[toast.variant],
        visible
          ? 'translate-x-0 opacity-100'
          : 'translate-x-full opacity-0',
      )}
    >
      {variantIcons[toast.variant]}

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-tight">{toast.title}</p>
        {toast.description && (
          <p className="text-xs mt-1 opacity-80 leading-snug">{toast.description}</p>
        )}
        {toast.action && (
          <button
            onClick={() => { toast.action!.onClick(); handleDismiss() }}
            className="text-xs font-semibold mt-1.5 underline underline-offset-2 hover:no-underline"
          >
            {toast.action.label}
          </button>
        )}
      </div>

      <button
        onClick={handleDismiss}
        className="shrink-0 p-0.5 rounded hover:bg-black/5 transition-colors"
        aria-label="Dismiss"
      >
        <FiX size={14} />
      </button>
    </div>
  )
}

// ── Toast Container (mount once in AppLayout) ─────────────────────────

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts)

  if (toasts.length === 0) return null

  return (
    <div
      className="fixed bottom-4 right-4 z-[2000] flex flex-col-reverse gap-2 pointer-events-auto"
      aria-label="Notifications"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  )
}
